import { InfoOutlined as IconInfo } from "@mui/icons-material";
import { IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2TransportationMethod } from "~/__generated__/graphql/types.ts";
import { formatKilometers, formatSeconds } from "~/modules/app/util/formatters";
import {
    FieldworkerAvailabilityByDate,
    FieldworkerAvailabilityDots,
} from "~/modules/fieldworker/components/FieldworkerAvailabilityDots";
import { GaiaExpandableMapMarkerHeader } from "~/modules/plannerV2/components/GaiaExpandableMapMarker/GaiaExpandableMapMarkerHeader";
import { IconFieldworker } from "~/modules/plannerV2/components/IconFieldworker";
import { mapPreferredToPlannerTransportationMethod } from "~/modules/plannerV2/util/mapPreferredToPlannerTransportationMethod";

import {
    GaiaExpandableMapMarkerFieldworkerAvailableTransportationMethodsFragment,
    GaiaExpandableMapMarkerFieldworkerHeaderFragment,
} from "./fragments.generated";

type GaiaExpandableMapMarkerFieldworkerHeaderProps = {
    fieldworker: GaiaExpandableMapMarkerFieldworkerHeaderFragment;
    availableTransportationMethods?: GaiaExpandableMapMarkerFieldworkerAvailableTransportationMethodsFragment[];
    availabilityByDate?: FieldworkerAvailabilityByDate;
    selectedDate: string;
    loading: boolean;
    isBeingAdded: boolean;
    isAssigned: boolean;
    onIconClick?: (transportationMethod: GqlPlannerV2TransportationMethod) => void;
};

export function GaiaExpandableMapMarkerFieldworkerHeader({
    fieldworker,
    availableTransportationMethods,
    availabilityByDate,
    selectedDate,
    loading,
    isBeingAdded,
    isAssigned,
    onIconClick,
}: GaiaExpandableMapMarkerFieldworkerHeaderProps) {
    const { t } = useTranslation("plannerV2");

    const defaultTransportationMethod = availableTransportationMethods?.find(
        availableTransportationMethod => availableTransportationMethod.isDefault,
    );

    const distanceExplanation = t("visitDetail.distanceExplanation", {
        travelMethod: t(`visitDetail.travelMethod.${fieldworker.preferredTransportationMethod}`),
    });

    return (
        <GaiaExpandableMapMarkerHeader>
            <IconButton
                onClick={() =>
                    onIconClick?.(mapPreferredToPlannerTransportationMethod(fieldworker.preferredTransportationMethod))
                }
                sx={{ padding: 0 }}
            >
                <IconFieldworker
                    variant={fieldworker.preferredTransportationMethod}
                    loading={isBeingAdded}
                    isAssigned={isAssigned}
                />
            </IconButton>
            {loading && (
                <>
                    <Skeleton width={50} />
                    <Skeleton width={50} />
                    <Skeleton width={50} />
                </>
            )}
            {loading === false && defaultTransportationMethod && availabilityByDate && (
                <>
                    <Stack direction="row" alignItems="center" columnGap={0.5}>
                        <Typography variant="body2">
                            {formatKilometers(defaultTransportationMethod.distanceInMeters)}km
                        </Typography>
                        <Tooltip title={distanceExplanation}>
                            <IconInfo fontSize="inherit" color="action" />
                        </Tooltip>
                    </Stack>
                    <Typography variant="body2">{formatSeconds(defaultTransportationMethod.durationInSeconds)}</Typography>

                    <FieldworkerAvailabilityDots availabilityByDate={availabilityByDate} selectedDate={selectedDate} />
                </>
            )}
        </GaiaExpandableMapMarkerHeader>
    );
}
